const DOC_TYPE_MAP = {
  "Kazakhstan ID": "1",
  "Kazakhstan residence permit": "33",
  "MRZ passport": "28",
  "Other passports": "42",
};

export const collectConsentData = (service, lang, logoUrl, postData) => {
  postData.pipeline.push({ service_name: "consent" });
  const titleParam = service.params.find((p) => p.id === "userTitle");
  const urlParam = service.params.find((p) => p.id === "url");
  const linkMaskParam = service.params.find((p) => p.id === "linkMask");
  const consentText =
    titleParam?.value && urlParam?.value && linkMaskParam?.value
      ? `${titleParam.value} [${linkMaskParam.value}](${urlParam.value})`
      : titleParam?.value ?? "";
  postData.ui = {
    logo_url: logoUrl,
    translation: { [lang]: { ConsentAgreement1: consentText } },
  };
};

export const collectPhoneVerificationData = (service, postData) => {
  let options = {};
  service.params.forEach((param) => {
    if (param.id === "allowFailure" && param.enabled) options.allow_failure = Boolean(param.value);
    if (param.id === "timeout" && param.enabled) options.timeout = param.value;
    if (param.id === "maxRetries" && param.enabled) options.max_retries = param.value;
    if (param.id === "operatorSelection" && param.enabled) {
      if (param.value === "all") {
        options.operators = ["all"];
      } else {
        const customOption = param.options.find((o) => o.value === "custom" && o.enabled);
        if (customOption?.params) {
          const selected = customOption.params.filter((o) => o.enabled).map((o) => o.value);
          if (selected.length > 0) options.operators = selected;
        }
      }
    }
  });
  postData.pipeline.push({ service_name: "veriphone", options });
};

export const collectVeridocData = (service, postData) => {
  let options = [];
  let config = {};
  let allow_failure = null;
  let timeout = null;
  let max_retries = null;
  let country_selector_value = null;

  service.params.forEach((param) => {
    if (
      param.enabled &&
      param.paramName !== "allow_failure" &&
      param.paramName !== "timeout" &&
      param.paramName !== "max_retries"
    ) {
      options.push(param.paramName);
      if (param.paramName === "scan") {
        param.params.forEach((innerParam) => {
          if (!innerParam.enabled) return;
          if (innerParam.paramName === "mirror_preview") {
            config.render = { mirror_preview: innerParam.enabled };
          } else if (innerParam.paramName === "doc_type") {
            const docTypeId = DOC_TYPE_MAP[innerParam.value] ?? null;
            config.doc_type = docTypeId;
          } else if (innerParam.paramName === "recognition_mode") {
            config.recognition_mode = innerParam.value === "One side" ? 0 : 1;
          } else if (innerParam.paramName === "country_code") {
            country_selector_value = innerParam.value;
          } else if (innerParam.type === "checkbox") {
            config[innerParam.paramName] = innerParam.enabled;
          } else {
            config[innerParam.paramName] = innerParam.value;
          }
        });
      }
    }
    if (param.paramName === "allow_failure") allow_failure = param.value === "true";
    else if (param.paramName === "timeout") timeout = param.value;
    else if (param.paramName === "max_retries") max_retries = param.value;
  });

  postData.pipeline.push({
    service_name: "veridoc",
    options,
    config,
    ...(allow_failure !== null && { allow_failure }),
    ...(timeout !== null && { timeout }),
    ...(max_retries !== null && { max_retries }),
    ...(country_selector_value !== null && { country_selector_value }),
  });
};

export const collectDeviceSwitchData = (service, postData) => {
  postData.pipeline.push(
    service.params.length > 0
      ? { service_name: "device-filter", qr_url: service.params[0].value, device_type: "mobile" }
      : { service_name: "device-filter", device_type: "mobile" }
  );
};

export const collectDocPreviewData = (service, postData, fileList = []) => {
  const files = [];
  const result = { service_name: service.paramName };

  service.params?.forEach((param) => {
    if (param.type === "checkbox" && param.paramName !== "includeClientCard") {
      result[param.paramName] = Boolean(param.enabled);
    } else if (param.type === "file-upload" && param.value) {
      files.push(param.value);
    }
    if (param.paramName === "includeClientCard" && param.enabled) {
      files.push("client-card");
    }
  });

  if (fileList.length > 0) files.push("input_files");
  if (files.length > 0) result.files = files;

  postData.pipeline.push(result);
};

export const collectEsignData = (service, postData, esignFileList = [], withFiles = true) => {
  const esignAction = service.paramName;
  if (esignAction === "esign-issue") {
    postData.pipeline.push({ service_name: "esign", action: "issue", skip_exists: true });
  } else if (esignAction === "esign-sign") {
    const files = [];
    if (esignFileList.length > 0) files.push("input_files");
    service.params.forEach((param) => {
      if (param.enabled && param.paramName === "includeClientCard") files.push("client-card");
    });
    postData.pipeline.push({
      service_name: "esign",
      action: "sign",
      ...(withFiles && files.length > 0 && { files }),
      "forgot-password-option": true,
    });
  }
};

export const collectRestData = (service, postData) => {
  const serviceObject = { service_name: service.paramName };
  service.params?.forEach((param) => {
    if (param.enabled) serviceObject[param.paramName] = param.value;
  });
  postData.pipeline.push(serviceObject);
};

export const collectData = (services, postData, options = {}) => {
  const { lang, logoUrl, fileList = [], esignFileList = [], withFiles = true } = options;
  services
    .filter((s) => s.isActive)
    .forEach((service) => {
      if (service.paramName === "consent") {
        collectConsentData(service, lang, logoUrl, postData);
      } else if (service.paramName === "veriphone") {
        collectPhoneVerificationData(service, postData);
      } else if (service.paramName === "veridoc") {
        collectVeridocData(service, postData);
      } else if (service.paramName === "device-filter") {
        collectDeviceSwitchData(service, postData);
      } else if (service.paramName === "doc-preview") {
        collectDocPreviewData(service, postData, fileList);
      } else if (service.paramName === "esign-issue" || service.paramName === "esign-sign") {
        collectEsignData(service, postData, esignFileList, withFiles);
      } else {
        collectRestData(service, postData);
      }
    });
};
